/** Sidebar navigation - fond graphite brun, items filtrés par rôle. */

import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Clock,
  Radio,
  CreditCard,
  CalendarDays,
  LogOut,
  UserCircle2,
  Building2,
  Truck,
  Route,
  SprayCan,
  ShieldCheck,
  BarChart3,
  ChefHat,
  UserCog,
  Briefcase,
  UtensilsCrossed,
  Snowflake,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
import { authApi } from '../../services/api';
import type { EmployeeActivity } from '../../types';

type Role = 'admin' | 'manager';

interface NavItem {
  to: string;
  icon: typeof Users;
  label: string;
  roles: Role[]; // rôles autorisés
  group?: string;
  // Page propre à une équipe : un chef d'équipe ne voit que celle de son équipe
  activity?: EmployeeActivity;
}

const NAV: NavItem[] = [
  { to: '/', icon: LayoutDashboard, label: 'Tableau de bord', roles: ['admin'] },
  { to: '/employees', icon: Users, label: 'Employés', roles: ['admin'] },
  { to: '/time-logs', icon: Clock, label: 'Pointages', roles: ['admin'] },
  { to: '/pointing-control', icon: BarChart3, label: 'Suivi des pointages', roles: ['admin'] },
  { to: '/badge-enroll', icon: CreditCard, label: 'Badges', roles: ['admin'] },

  // --- Section plannings (seule section visible d'un chef d'équipe) ---
  { to: '/assignment', icon: CalendarDays, label: 'Affectation', roles: ['admin', 'manager'], group: 'Plannings' },
  { to: '/schedules/boulangerie', icon: ChefHat, label: 'Boulangerie', roles: ['admin', 'manager'], group: 'Plannings', activity: 'boulangerie' },
  { to: '/schedules/traiteur', icon: UtensilsCrossed, label: 'Traiteur', roles: ['admin', 'manager'], group: 'Plannings', activity: 'traiteur' },
  { to: '/schedules/chambre-froide', icon: Snowflake, label: 'Chambre froide', roles: ['admin', 'manager'], group: 'Plannings', activity: 'chambre_froide' },
  { to: '/schedules/cleaning', icon: SprayCan, label: 'Nettoyage', roles: ['admin', 'manager'], group: 'Plannings', activity: 'cleaning' },
  { to: '/schedules/office', icon: Briefcase, label: 'Bureau', roles: ['admin', 'manager'], group: 'Plannings', activity: 'office' },

  // --- Section configuration (admin uniquement) ---
  { to: '/sites', icon: Building2, label: 'Sites', roles: ['admin'], group: 'Configuration' },
  { to: '/readers', icon: Radio, label: 'Bornes', roles: ['admin'], group: 'Configuration' },
  { to: '/vehicles', icon: Truck, label: 'Véhicules', roles: ['admin'], group: 'Configuration' },
  { to: '/rounds', icon: Route, label: 'Tournées', roles: ['admin'], group: 'Configuration' },
  { to: '/cleaning-zones', icon: SprayCan, label: 'Zones nettoyage', roles: ['admin'], group: 'Configuration' },
  { to: '/epis', icon: ShieldCheck, label: 'EPI', roles: ['admin'], group: 'Configuration' },
  { to: '/users', icon: UserCog, label: 'Utilisateurs', roles: ['admin'], group: 'Configuration' },
];

interface SidebarProps {
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps = {}) {
  const { logout } = useAuth();
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => authApi.me(),
    staleTime: 60_000,
  });
  const role = (me?.role ?? 'admin') as Role;
  // Équipe du chef connecté (null = non restreint : il voit toutes les équipes)
  const team = role === 'manager' ? me?.activity ?? null : null;

  const items = NAV.filter(
    (it) => it.roles.includes(role) && (!it.activity || !team || it.activity === team),
  );

  // Groupage : items principaux d'abord, puis groupes
  const main = items.filter((it) => !it.group);
  const groups: Record<string, NavItem[]> = {};
  for (const it of items) {
    if (it.group) (groups[it.group] ??= []).push(it);
  }

  return (
    <aside className="w-60 bg-sidebar flex flex-col min-h-screen text-stone-300">
      <div className="px-5 py-5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-white font-bold text-sm">
            P
          </div>
          <div>
            <h1 className="text-white font-bold text-base leading-tight">PointBreak</h1>
            <p className="text-[11px] text-stone-400">
              {role === 'manager' ? 'Chef d\'équipe' : 'Administration'}
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {main.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-white/10 text-white font-medium border-l-2 border-accent pl-[10px]'
                  : 'text-stone-300 hover:bg-sidebar-hover hover:text-white'
              )
            }
          >
            <Icon size={16} />
            {label}
          </NavLink>
        ))}

        {Object.entries(groups).map(([group, gItems]) => (
          <div key={group} className="pt-4">
            <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
              {group}
            </div>
            {gItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-white/10 text-white font-medium border-l-2 border-accent pl-[10px]'
                      : 'text-stone-300 hover:bg-sidebar-hover hover:text-white'
                  )
                }
              >
                <Icon size={16} />
                {label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="px-3 pb-4 pt-3 border-t border-white/5 space-y-1">
        <div className="px-3 py-1.5 text-[11px] text-stone-400 flex items-center gap-2">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          Backend opérationnel
        </div>
        <NavLink
          to="/profile"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
              isActive
                ? 'bg-white/10 text-white font-medium'
                : 'text-stone-400 hover:bg-sidebar-hover hover:text-white'
            )
          }
        >
          <UserCircle2 size={16} />
          Mon profil
        </NavLink>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm
                     text-stone-400 hover:bg-sidebar-hover hover:text-white
                     transition-colors w-full"
        >
          <LogOut size={16} />
          Déconnexion
        </button>
      </div>
    </aside>
  );
}
